import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Phone, Calendar, MessageSquare, Plus, Filter, Mic, MicOff, Maximize2, Minimize2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPE_ICONS = { email: '📧', call: '📞', meeting: '📅' };
const SENTIMENT_COLORS = { positive: 'bg-emerald-50 text-emerald-700', neutral: 'bg-stone-50 text-stone-600', negative: 'bg-red-50 text-red-700' };

/* ── Voice-to-Text Button ────────────────────────────────────────────────── */
function VoiceBtn({ onResult, testId, isTextarea }) {
    const [listening, setListening] = useState(false);

    const toggle = () => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) { toast.error('Voice input not supported in this browser'); return; }
        if (listening) { setListening(false); return; }

        const recog = new SR();
        recog.continuous = false;
        recog.interimResults = false;
        recog.lang = 'en-US';
        recog.onresult = (e) => { onResult(e.results[0][0].transcript); setListening(false); };
        recog.onerror = () => { setListening(false); toast.error('Voice recognition failed'); };
        recog.onend = () => setListening(false);
        recog.start();
        setListening(true);
    };

    return (
        <button type="button" onClick={toggle} data-testid={testId}
            title={listening ? 'Stop listening' : 'Voice input'}
            className={`absolute right-2 ${isTextarea ? 'top-2' : 'top-1/2 -translate-y-1/2'} p-1.5 rounded-lg transition-colors ${
                listening ? 'bg-red-100 text-red-600 animate-pulse' : 'text-stone-400 hover:text-stone-700 hover:bg-stone-100'
            }`}>
            {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </button>
    );
}

function CommModal({ open, onClose, accounts, onSave }) {
    const { getAuthHeader } = useAuth();
    const [form, setForm] = useState({ type: 'email', direction: 'outbound', subject: '', body: '', from_email: '', to_emails: [], account_id: '', duration_minutes: '', meeting_outcome: '' });
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const handleSave = async () => {
        if (!form.subject && !form.body) return toast.error('Subject or body required');
        setLoading(true);
        try {
            const payload = { ...form, to_emails: form.to_emails_str?.split(',').map(e => e.trim()).filter(Boolean) || [] };
            const res = await axios.post(`${API}/communications`, payload, { headers: getAuthHeader() });
            toast.success('Communication logged');
            onSave(res.data);
            onClose();
        } catch { toast.error('Failed to log'); }
        finally { setLoading(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className={`transition-all duration-300 ${expanded ? 'max-w-3xl' : 'max-w-lg'}`}>
                <DialogHeader>
                    <div className="flex items-center justify-between">
                        <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Log Communication</DialogTitle>
                        <button onClick={() => setExpanded(prev => !prev)}
                            className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors"
                            data-testid="expand-comm-modal-btn" title={expanded ? 'Collapse' : 'Expand'}>
                            {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                        </button>
                    </div>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                    <div className={expanded ? 'grid grid-cols-3 gap-3' : 'grid grid-cols-2 gap-3'}>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Type</label>
                            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} data-testid="comm-type-select"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none capitalize">
                                {['email', 'call', 'meeting'].map(t => <option key={t} className="capitalize">{t}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Direction</label>
                            <select value={form.direction || 'outbound'} onChange={e => setForm({ ...form, direction: e.target.value })} data-testid="comm-direction-select"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none capitalize">
                                {['inbound', 'outbound'].map(d => <option key={d} className="capitalize">{d}</option>)}
                            </select>
                        </div>
                        {expanded && (
                            <div>
                                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Company</label>
                                <select value={form.account_id || ''} onChange={e => setForm({ ...form, account_id: e.target.value })} data-testid="comm-account-select"
                                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none">
                                    <option value="">Select account...</option>
                                    {accounts.map(a => <option key={a.account_id} value={a.account_id}>{a.name}</option>)}
                                </select>
                            </div>
                        )}
                    </div>
                    {!expanded && (
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Company</label>
                            <select value={form.account_id || ''} onChange={e => setForm({ ...form, account_id: e.target.value })} data-testid="comm-account-select"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none">
                                <option value="">Select account...</option>
                                {accounts.map(a => <option key={a.account_id} value={a.account_id}>{a.name}</option>)}
                            </select>
                        </div>
                    )}
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Subject</label>
                        <div className="relative">
                            <input value={form.subject || ''} onChange={e => setForm({ ...form, subject: e.target.value })}
                                data-testid="comm-subject-input"
                                className="w-full px-3 py-2 pr-10 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                            <VoiceBtn onResult={(text) => setForm(f => ({ ...f, subject: (f.subject ? f.subject + ' ' : '') + text }))} testId="voice-comm-subject-btn" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Body / Notes</label>
                        <div className="relative">
                            <textarea value={form.body || ''} onChange={e => setForm({ ...form, body: e.target.value })} rows={expanded ? 8 : 3}
                                className="w-full px-3 py-2 pr-10 text-sm border border-stone-200 rounded-lg focus:outline-none resize-none" />
                            <VoiceBtn onResult={(text) => setForm(f => ({ ...f, body: (f.body ? f.body + ' ' : '') + text }))} testId="voice-comm-body-btn" isTextarea />
                        </div>
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-4">
                    <button onClick={onClose} className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700">Cancel</button>
                    <button onClick={handleSave} disabled={loading} data-testid="comm-save-btn"
                        className="px-4 py-2 text-sm bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50">
                        {loading ? 'Saving...' : 'Log Communication'}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function Communications() {
    const { getAuthHeader } = useAuth();
    const [comms, setComms] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [typeFilter, setTypeFilter] = useState('all');

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const [cRes, aRes, iRes] = await Promise.all([
                axios.get(`${API}/communications`, { headers: getAuthHeader() }),
                axios.get(`${API}/accounts`, { headers: getAuthHeader() }),
                axios.get(`${API}/integrations`, { headers: getAuthHeader() }),
            ]);
            setComms(Array.isArray(cRes.data) ? cRes.data : (cRes.data?.data || []));
            setAccounts(Array.isArray(aRes.data) ? aRes.data : (aRes.data?.data || []));
            setIntegrations(iRes.data);
        } catch {} finally { setLoading(false); }
    };

    const handleConnect = async (type) => {
        try {
            await axios.post(`${API}/integrations/${type}/connect`, { connected: true }, { headers: getAuthHeader() });
            toast.success(`${type} connected successfully`);
            loadData();
        } catch { toast.error(`Failed to connect ${type}`); }
    };

    const handleSave = (saved) => setComms(prev => [saved, ...prev]);

    const getAccountName = (id) => accounts.find(a => a.account_id === id)?.name;

    const filtered = typeFilter === 'all' ? comms : comms.filter(c => c.type === typeFilter);
    const gmailStatus = integrations.find(i => i.type === 'gmail')?.status;
    const outlookStatus = integrations.find(i => i.type === 'outlook')?.status;

    return (
        <div className="space-y-6">
            {/* Integration Status */}
            <div className="grid grid-cols-2 gap-4">
                {[
                    { type: 'gmail', label: 'Gmail', icon: '📧', color: 'stone', status: gmailStatus },
                    { type: 'outlook', label: 'Outlook', icon: '📨', color: 'blue', status: outlookStatus },
                ].map(int => (
                    <div key={int.type} className="bg-white rounded-xl border border-stone-200 p-5">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="text-2xl">{int.icon}</div>
                                <div>
                                    <p className="font-medium text-stone-900 text-sm">{int.label}</p>
                                    <p className={`text-xs ${int.status === 'connected' ? 'text-emerald-600' : 'text-stone-400'}`}>
                                        {int.status === 'connected' ? 'Connected' : 'Not connected'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => handleConnect(int.type)}
                                data-testid={`connect-${int.type}-btn`}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${int.status === 'connected' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-stone-300 bg-stone-100 text-stone-900 hover:bg-stone-100'}`}>
                                {int.status === 'connected' ? 'Connected' : 'Connect'}
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Toolbar */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex gap-2">
                    {['all', 'email', 'call', 'meeting'].map(t => (
                        <button key={t} onClick={() => setTypeFilter(t)}
                            className={`px-3 py-1.5 text-sm rounded-lg capitalize transition-colors ${typeFilter === t ? 'bg-stone-900 text-white' : 'bg-white border border-stone-200 text-stone-600 hover:bg-stone-50'}`}>
                            {t}
                        </button>
                    ))}
                </div>
                <button onClick={() => setShowModal(true)} data-testid="log-comm-btn"
                    className="flex items-center gap-2 px-4 py-2 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 shadow-sm">
                    <Plus className="w-4 h-4" /> Log Communication
                </button>
            </div>

            {/* Communications Timeline */}
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="communications-list">
                {loading ? <div className="p-12 text-center text-stone-400">Loading...</div> :
                filtered.length === 0 ? (
                    <div className="p-12 text-center">
                        <MessageSquare className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                        <p className="text-stone-500 text-sm mb-3">No communications logged</p>
                        <button onClick={() => setShowModal(true)} className="text-stone-700 text-sm">Log your first communication</button>
                    </div>
                ) : (
                    <div className="divide-y divide-stone-100">
                        {filtered.map(c => (
                            <div key={c.comm_id} className="flex items-start gap-4 px-5 py-4 hover:bg-stone-50 group transition-colors" data-testid={`comm-${c.comm_id}`}>
                                <div className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0 text-base">
                                    {TYPE_ICONS[c.type] || '📌'}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <p className="text-sm font-medium text-stone-900">{c.subject || `${c.type} communication`}</p>
                                        {c.direction && (
                                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${c.direction === 'inbound' ? 'bg-blue-50 text-blue-600' : 'bg-stone-100 text-stone-700'}`}>
                                                {c.direction}
                                            </span>
                                        )}
                                        {c.sentiment && (
                                            <span className={`text-xs px-1.5 py-0.5 rounded-full capitalize ${SENTIMENT_COLORS[c.sentiment] || ''}`}>{c.sentiment}</span>
                                        )}
                                    </div>
                                    {c.body && <p className="text-xs text-stone-500 truncate max-w-lg">{c.body}</p>}
                                    <div className="flex items-center gap-3 mt-1">
                                        {c.account_id && <span className="text-xs text-stone-400">{getAccountName(c.account_id)}</span>}
                                        <span className="text-xs text-stone-400">{new Date(c.comm_date || c.created_at).toLocaleDateString()}</span>
                                        <span className="text-xs text-stone-400 capitalize">{c.source || 'manual'}</span>
                                    </div>
                                </div>
                                {c.ai_summary && (
                                    <div className="w-48 text-xs text-violet-600 bg-violet-50 p-2 rounded-lg border border-violet-100 flex-shrink-0">
                                        <p className="font-medium mb-0.5">AI Summary</p>
                                        <p className="text-violet-500">{c.ai_summary}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <CommModal open={showModal} onClose={() => setShowModal(false)} accounts={accounts} onSave={handleSave} />
        </div>
    );
}
